// Water recipe — a STATIC (non-animated) water surface. Immutable built-in root (never mutate —
// extend/fork instead).
//
// DESIGN CONSTRAINT (2026-08-09): procedural-materials must stay deterministic for a given camera
// position + zoom, so there is NO time uniform available and therefore no wave animation. What
// still reads convincingly as water WITHOUT motion:
//   1. A LOW roughness (0 — the mirror setting: the shader clamps at 0.02, giving exponent 8192,
//      the tightest lobe the material system allows). Water's single strongest cue is the tight
//      specular lobe; everything else here exists to give that lobe a plausible NORMAL field to
//      break up.
//   2. A layered wave spectrum (swell -> chop -> capillary), each an order of magnitude smaller in
//      both wavelength and relief than the one above it. Real water's give-away is that its
//      highlight structure is multi-scale; a single wave train reads as corrugated plastic.
//   3. Deep/shallow COLOUR variation at metres scale (the depth layer) — flat-tinted blue reads as
//      painted glass. The colour must vary independently of the wave relief.
//   4. Sparse foam flecks whose placement is gated by the accumulated height (below).
//
// SCALE CONTRACT (every length is in metres, per the recipe convention):
//   bed        ~3 m turbulence, ~4 cm — height ONLY, and the ONLY strictly-NON-NEGATIVE height
//              layer in the stack. See "THE FOAM GATE" below for why it is load-bearing.
//   depthFromElevation  LARGE-SCALE (km) colour — the terrain height (u_geoHeight) mapped through
//              `elevation_ramp`; the fix for water flattening to a single colour at far zoom.
//              `elevationBlend` scales it (0 = off, byte-identical).  See the layer comment below.
//   depth      ~14 m fbm, COLOUR only — the deep-navy -> shallow-teal albedo covering the whole
//              surface (the sand-recipe `base` precedent: a material MUST have one layer that
//              paints its albedo everywhere, or the troughs show the default grey through).
//   skyTint    the lightModulatedRamp pass: unlit fragments deepen toward abyssal navy. Re-samples
//              the depth ramp as its own colour source (the snow `shadowTint` precedent — a
//              lightModulatedRamp layer REPLACES the accumulated colour, so it has to reproduce
//              the colour it is tinting). It therefore sits BEFORE every colour MODULATOR.
//   swell      ~6 m wavelength, ~35 cm relief — the dominant swell. Blends `multiply` (not
//              `over`) so its near-white ramp MODULATES the depth colour instead of replacing it;
//              this is what makes the swell shading and the depth colour coexist.
//   chop       ~1.5 m wavelength, ~3 cm — the wind chop riding the swell. Height ONLY: a second
//              colour-writing wave train would fight the swell's shading for no gain.
//   capillary  ~0.25 m fbm, ~6 mm — the finest ripple texture, height only. Keeps the specular
//              lobe alive when the camera is close enough that the chop resolves as broad shapes.
//   foam       ~0.8 m coverage field, ~3 cm — connected ragged streaks aligned with the swell
//              (Set11 Phase 3 — replaced the old scatter lattice of evenly-spaced round flecks).
//
// THE FOAM GATE (the subtle bit — read before changing any relief value):
//   `heightBlend` weights a layer's colour by smoothstep(0, softness, L.a - hBelow), where hBelow
//   is the height accumulated BELOW it. The `waves` primitive emits a CENTRED height ((v - 0.5) *
//   heightScale) precisely so a wave train adds no DC offset — which means hBelow can go NEGATIVE.
//   Outside a foam disc L.a is exactly 0, so a negative hBelow makes `L.a - hBelow` POSITIVE and
//   the gate paints foam white across the whole trough. That failure mode is a uniform white wash,
//   and no amount of softness tuning fixes it (a large softness merely spreads the wash).
//   The `bed` layer is the fix: mode 3 (turbulence) returns [0,1], so its height contribution is
//   non-negative and lifts hBelow clear of zero over the swell's negative excursion. It also earns
//   its keep visually — because bed varies at ~3 m, `foam_height` sitting inside the bed's range
//   makes foam appear in broad meandering PATCHES (where the bed is low) rather than as an even
//   stipple, which is how real foam streaks read.
//   Invariant to preserve: bed_height > (swell_height + chop_height) / 2, and foam_height inside
//   the bed's 0..bed_height range (otherwise foam is either everywhere or nowhere).
//
// Set11 Phase 3 rewiring (2026-08-09): the foam layer's PRIMITIVE changed from scatter to
// coverageField — the params that described the scatter lattice (foam_density / foam_presence /
// the scatter radius spread) are GONE and replaced by the coverage-field params (foam_scale /
// foam_streak / foam_threshold / foam_warpScale / foam_warpAmount).  The foam layer still blends
// heightBlend with the SAME bed gate invariant — only the spatial pattern generator changed.
//
// FOAM REMOVED (2026-08-09, recoverable from git): coverageField's output is a SATURATING
// smoothstep, and layerBody feeds it to both `rampVal(cov, ramp)` and `cov * heightScale` — so
// patch interiors clamp to 1.0 and foam renders as flat-white opaque slabs, untunable by any param.
// Re-enable once the primitive also emits the PRE-threshold field (ramp indexed by that, mask as a
// multiplier). Cheaper interim options: expose `edgeSoftness` (~0.3+ stops cov saturating), or set
// foam_height far below bed_height so heightBlend weights foam partially (a wash, not a slab).
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";
import {colorRamp} from "../RecipeUtils.js";

export const WATER_RECIPE_ID = "water";

export const waterRecipe = new MaterialRecipe({
	id: WATER_RECIPE_ID,
	name: "Water",
	roughness: 0.4,
	// Full specular (2026-08-09): roughnessAwareSpecular is enabled, so `roughness` 0 saturates the
	// shader's 8192-exponent clamp — an extremely tight glint. Full strength (1) so it still lands
	// visibly on the wave slopes.
	specStrength: 10,
	// Water is LEVEL: no large-scale slope of its own, so the terrain's geo normal should not shade it.
	// Also fixes glints that swam when the camera panned — u_geoNormal derives from the camera-dependent
	// working-grid solve, and a tight lobe sits on the steep part of pow(), amplifying sub-degree wobble.
	flatGeoNormal: true,
	// LOAD-BEARING for specular: top-down V = +z puts H at (90-alt)/2 off vertical, so a 30deg sun demands
	// 15deg slopes -- 10x this recipe's wave slopes (0.02-0.06) => zero glint. 55 demands 17.5deg, whose
	// pow() tail the waves DO reach. Water-only, so surrounding terrain keeps its authored low sun.
	lightAltitudeOverride: 55,
	params: [
		// --- bed: the height-chain pedestal (see THE FOAM GATE) ---
		// Inert without foam: just gentle relief, kept as the only non-negative height layer so
		// re-enabling foam needs no height-chain rework (bed_height > (swell_height + chop_height)/2).
		new ParamSchema({key: "bed_scale", label: "Foam-patch scale (m)", kind: "scalar", min: 0.5, max: 40, default: 8}),
		new ParamSchema({key: "bed_height", label: "Height pedestal (m)", kind: "scalar", min: 0.005, max: 0.2, default: 0.06}),

		// --- depth: the base albedo ---
		new ParamSchema({key: "depth_scale", label: "Depth variation scale (m)", kind: "scalar", min: 1, max: 200, default: 14}),
		// Retuned 2026-08-09 from an oblique navy->teal ramp to a top-down ramp: stops sit close because
		// open water is near-uniform in hue from above. Some spread remains so `depth` is not a flat fill
		// (and `skyTint`, which re-samples this, has something to tint).
		new ParamSchema({key: "water_ramp", label: "Water depth ramp", kind: "ramp", default: colorRamp([
			{position: 0  , color_str: `rgb(0,27,52)`},
			{position: 0.45, color_str: `rgb(0,53,84)`},
			{position: 0.75, color_str: `rgb(6,83,122)`},
			{position: 1  , color_str: `rgb(20,115,158)`},
		])}),

		// --- depthFromElevation: the LARGE-SCALE depth colour (2026-08-13) ---
		// This is the fix for "water devolves to a single colour at far zoom".  The `depth` layer
		// above is a 14 m fbm that fades to its ramp MEAN at map-overview — leaving flat navy.  The
		// elevation-reactive layer is genuinely LARGE-SCALE (km-scale, not procedural): it maps the
		// actual terrain HEIGHT (u_geoHeight lattice) through `elevation_ramp`, so deep seabed → deep
		// navy and shallow shelf → teal, at any zoom.  `elevationBlend` (the slider) controls how much
		// of this shows: 0 = the pre-2026-08-13 output exactly (byte-parity), 1 = full elevation
		// colour, in-between = a mix.  The layer sits FIRST (before `depth`/`skyTint`) so the fine
		// layers modulate it, and uses `over` + the generic `blendFactor` blend-opacity.
		new ParamSchema({key: "elevationBlend", label: "Elevation visibility", kind: "scalar", min: 0, max: 1, default: 0.5}),
		// The elevation→colour ramp.  t maps minHeight→maxHeight (world metres); the shader clamps,
		// so out-of-range elevations use the nearest end.  Authored as deep-navy (low) → teal (high)
		// so shallow shelves/lakebeds read bright; adjust the stops to the terrain's real range.
		new ParamSchema({key: "elevation_min", label: "Elevation ramp min (m)", kind: "scalar", min: -2000, max: 0, default: -100}),
		new ParamSchema({key: "elevation_max", label: "Elevation ramp max (m)", kind: "scalar", min: -2000, max: 0, default: 0}),
		new ParamSchema({key: "elevation_ramp", label: "Elevation depth ramp", kind: "ramp", default: colorRamp([
			{position: 0  , color_str: `rgb(-5,22,61)`},
			{position: 0.5, color_str: `rgb(-5,58,92)`},
			{position: 1  , color_str: `rgb(20,116,158)`},
		])}),

		// --- skyTint: the unlit-side deepening ---
		// Not a "shadow" in the snow sense: water's dark side is dark because it is reflecting less
		// sky, so the tint target is an abyssal navy rather than snow's cool blue lift.
		new ParamSchema({key: "shadow_color", label: "Unlit water tint", kind: "color", default: [0.03, 0.10, 0.18]}),
		// Lowered 0.5 -> 0.2 (2026-08-09): the surface is near-uniform from above, so a strong unlit
		// tint darkens broad regions into blotches that read as cloud shadow rather than as water.
		new ParamSchema({key: "shadow_amount", label: "Unlit tint amount", kind: "scalar", min: 0, max: 1, default: 0.2}),

		// --- swell: the dominant wave train ---
		// Raised 2.6 -> 6 (2026-08-09): at the zoom this material is actually being viewed at (tens of
		// metres across the frame) a 2.6 m train sits at ~1 px and contributes nothing but aliasing.
		new ParamSchema({key: "swell_wavelength", label: "Swell spacing (m)", kind: "scalar", min: 0.2, max: 40, default: 6}),
		new ParamSchema({key: "swell_angle", label: "Swell angle (deg)", kind: "scalar", min: 0, max: 360, default: 28}),
		// A pure sinusoid (1.0). Any crest narrowing reads as hard parallel corrugation — water's
		// swell profile is broad and rounded; the CROSS train supplies the irregularity instead.
		new ParamSchema({key: "swell_sharpness", label: "Swell sharpness", kind: "scalar", min: 0.05, max: 8, default: 1}),
		// Set3 §3.4 — the crossing train. Kept low-strength (a perturbation, not a partner): mixed
		// near-equally, two sinusoids only peak where BOTH peak, which produces an intersection
		// lattice of dashes rather than meandering crests (the sand-recipe lesson).
		new ParamSchema({key: "swell_crossAngle", label: "Cross-swell angle (deg)", kind: "scalar", min: 0, max: 360, default: 62}),
		new ParamSchema({key: "swell_crossStrength", label: "Cross-swell strength", kind: "scalar", min: 0, max: 1, default: 0.3}),
		// MUST stay incommensurate with 1.0 or the two trains beat into a rigid moire grid.
		new ParamSchema({key: "swell_crossRatio", label: "Cross-swell wavelength ratio", kind: "scalar", min: 0.1, max: 8, default: 1.7}),
		// Water swell is nearly symmetric (unlike a sand ripple's lee/stoss asymmetry); only
		// breaking waves are strongly asymmetric, and those need animation to be believable.
		new ParamSchema({key: "swell_asymm", label: "Swell asymmetry", kind: "scalar", min: 1, max: 8, default: 1.3}),
		// The warp is the ONLY source of irregularity in a sinusoid; its SCALE must stay well ABOVE
		// the wavelength (~5x). AMOUNT 7 (2026-08-09) exceeds the old 0.5x-wavelength contract to
		// break crests into meandering, chop-broken segments instead of regular corduroy bands.
		new ParamSchema({key: "swell_warpScale", label: "Swell warp scale (m)", kind: "scalar", min: 0.5, max: 100, default: 30}),
		new ParamSchema({key: "swell_warpAmount", label: "Swell warp amount (m)", kind: "scalar", min: 0, max: 10, default: 7}),
		// MULTIPLY ramp — these are near-white MULTIPLIERS over the depth colour, not colours in
		// their own right. The dark end is a shadowed swell flank; anything below ~0.75 grey turns
		// the water muddy, since it multiplies an already-dark base.
		new ParamSchema({key: "swell_ramp", label: "Swell shading ramp", kind: "ramp", default: colorRamp([
			{position: 0, color_str: `rgb(196,206,216)`},   // trough flank (self-shadowed)
			{position: 0.55, color_str: `rgb(236,241,246)`}, // mid
			{position: 1, color_str: `rgb(255,255,255)`},    // crest (unmodulated)
		])}),
		// AMPLITUDES ARE SLOPES (2026-08-09): shading responds to amp/wavelength, not to amp. The whole
		// spectrum was ~10-30x too shallow (0.2-0.9 deg of tilt = shading-invisible), so the surface
		// rendered flat. Target band ~0.05-0.15 (real wind chop ~0.05-0.1); here 0.35/6 = 0.058.
		new ParamSchema({key: "swell_height", label: "Swell relief (m)", kind: "scalar", min: 0, max: 0.5, default: 0.35}),

		// --- chop: the wind chop, height only ---
		// DOMINANT surface cue. 1.5 m (2026-08-09, up from 0.9) resolves cleanly at the 32 m view
		// (0.9 m aliased at ~1 px); slope 0.03/1.5 = 0.02, shallower than the old 0.078.
		new ParamSchema({key: "chop_wavelength", label: "Chop spacing (m)", kind: "scalar", min: 0.05, max: 5, default: 1.5}),
		// Wind chop runs at an angle to the swell (~50° off here) — that non-alignment is what makes
		// the two spectra read as separate, rather than as one train with extra harmonics.
		new ParamSchema({key: "chop_angle", label: "Chop angle (deg)", kind: "scalar", min: 0, max: 360, default: 78}),
		new ParamSchema({key: "chop_crossAngle", label: "Cross-chop angle (deg)", kind: "scalar", min: 0, max: 360, default: 143}),
		new ParamSchema({key: "chop_crossStrength", label: "Cross-chop strength", kind: "scalar", min: 0, max: 1, default: 0.4}),
		new ParamSchema({key: "chop_crossRatio", label: "Cross-chop wavelength ratio", kind: "scalar", min: 0.1, max: 8, default: 2.3}),
		new ParamSchema({key: "chop_warpScale", label: "Chop warp scale (m)", kind: "scalar", min: 0.1, max: 20, default: 5}),
		new ParamSchema({key: "chop_warpAmount", label: "Chop warp amount (m)", kind: "scalar", min: 0, max: 5, default: 2.5}),
		// 0.03/1.5 = 0.02 slope (2026-08-09 override set: shallower than the old 0.078). The warp
		// (2.5 m vs the 1.5 m wavelength) now does the irregularity work instead of the relief.
		new ParamSchema({key: "chop_height", label: "Chop relief (m)", kind: "scalar", min: 0, max: 0.1, default: 0.03}),

		// --- capillary: the finest ripple, height only ---
		// 0.25 m / 0.006 m = 0.024 slope (2026-08-09): a gentle near-mirror ripple; the old 0.10
		// slope caused sparkle aliasing under the roughness-0 lobe, so the capillary was tamed.
		new ParamSchema({key: "capillary_scale", label: "Capillary ripple scale (m)", kind: "scalar", min: 0.01, max: 1, default: 0.25}),
		new ParamSchema({key: "capillary_height", label: "Capillary relief (m)", kind: "scalar", min: 0, max: 0.02, default: 0.006}),

		// --- foam --- DISABLED 2026-08-09 (see the FOAM DISABLED note at the top of this file):
		// coverageField's saturating threshold flattens patch interiors to opaque white slabs.
		// Kept commented so re-enabling is a pure uncomment once the primitive pre-thresholds.
		// oxlint-disable-next-line lf/sc
		/*
		// Set11 Phase 3 — foam is now a COVERAGE FIELD, not a scatter lattice: connected, ragged,
		// streaky regions that align with the swell (anisotropy stretched along swell_angle).
		new ParamSchema({key: "foam_scale", label: "Foam patch scale (m)", kind: "scalar", min: 0.05, max: 20, default: 0.8}),
		new ParamSchema({key: "foam_streak", label: "Foam streak anisotropy", kind: "scalar", min: 1, max: 50, default: 6}),
		new ParamSchema({key: "foam_threshold", label: "Foam threshold", kind: "scalar", min: 0, max: 1, default: 0.62}),
		new ParamSchema({key: "foam_warpScale", label: "Foam warp scale (m)", kind: "scalar", min: 0.05, max: 100, default: 3}),
		new ParamSchema({key: "foam_warpAmount", label: "Foam warp amount (m)", kind: "scalar", min: 0, max: 20, default: 0.6}),
		// Foam is NOT white: it is bubble-scattered water, so it keeps a faint cool cast and its
		// bright end still sits below 255 (a clipped white fleck reads as a UI artifact).
		new ParamSchema({key: "foam_ramp", label: "Foam ramp", kind: "ramp", default: colorRamp([
			{position: 0, color_str: `rgb(205,220,226)`},
			{position: 1, color_str: `rgb(248,252,253)`},
		])}),
		// Must sit INSIDE the bed's 0..bed_height range (see THE FOAM GATE): at/above bed_height
		// foam covers everything; at ~0 it never shows.
		new ParamSchema({key: "foam_height", label: "Foam relief (m)", kind: "scalar", min: 0, max: 0.1, default: 0.026}),
		// A FRACTION of foam_height — a softness at or above the height it gates smears the gate
		// into a full-coverage wash (the exact plain-`over` behaviour heightBlend exists to avoid).
		new ParamSchema({key: "foam_softness", label: "Foam blend softness", kind: "scalar", min: 0.0001, max: 0.05, default: 0.007}),
		*/
	],
	layers: [
		// bed — height ONLY, non-negative (mode 3 = turbulence, [0,1]). Keeps hBelow positive for
		// the foam gate; metres-scale variation turns foam into broad patches, not an even stipple.
		// NOT a colour layer: metres-scale colour blotches would fight the depth ramp's role.
		new MaterialLayer({
			id: "bed",
			primitive: "fbm",
			args: {
				scale: {ref: "bed_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 3,
				heightScale: {ref: "bed_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		// depth — the water ALBEDO over the whole surface. Colour only; the relief is the wave
		// layers' job, and metres-scale relief here would swamp the swell in the height chain.
		new MaterialLayer({
			id: "depth",
			primitive: "fbm",
			args: {
				scale: {ref: "depth_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "water_ramp"},
				heightScale: 0,
			},
			blend: "over",
			contributes: "color",
		}),
		// skyTint — the unlit-side deepening. MUST precede every colour MODULATOR: its
		// lightModulatedRamp blend REPLACES the accumulated colour, so prior multipliers are
		// discarded. It reuses the depth ramp/scale to reproduce the depth colour, then tints it.
		new MaterialLayer({
			id: "skyTint",
			primitive: "fbm",
			args: {
				scale: {ref: "depth_scale"},
				octaves: 4,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				colorRamp: {ref: "water_ramp"},
				heightScale: 0,
			},
			blend: "lightModulatedRamp",
			blendArgs: {shadowColor: {ref: "shadow_color"}, amount: {ref: "shadow_amount"}},
			contributes: "color",
		}),
		// depthFromElevation — the LARGE-SCALE depth colour (2026-08-13).  `geoHeight` maps the
		// terrain height (u_geoHeight) through `elevation_ramp`.  `over` + `blendFactor` so the
		// "elevation visibility" slider (0 = current output, 1 = full elevation, in-between = mix)
		// scales how much of this replaces the accumulated base.  Placed AFTER `depth`/`skyTint`
		// (both use `over`/`lightModulatedRamp`, which would otherwise overwrite it) and BEFORE the
		// wave MODULATORS (`swell` etc.), so at factor > 0 the elevation colour is the base that
		// swell/chop modulate over.  Default 0 → `blendFactor` 0 = a no-op (mix toward itself at
		// weight 0), so the stack is byte-identical to before.
		new MaterialLayer({
			id: "depthFromElevation",
			primitive: "geoHeight",
			args: {
				minHeight: {ref: "elevation_min"},
				maxHeight: {ref: "elevation_max"},
				colorRamp: {ref: "elevation_ramp"},
				heightScale: 0,
			},
			blend: "over",
			blendArgs: {blendFactor: {ref: "elevationBlend"}},
			contributes: "color",
		}),
		// swell — the dominant train. `multiply`, NOT `over`: `over` would replace the depth colour
		// with the swell ramp outright (losing all deep/shallow variation), which is why the ramp
		// here is authored as near-white multipliers rather than as water colours.
		new MaterialLayer({
			id: "swell",
			primitive: "waves",
			args: {
				wavelength: {ref: "swell_wavelength"},
				angle: {ref: "swell_angle"},
				sharpness: {ref: "swell_sharpness"},
				crossAngle: {ref: "swell_crossAngle"},
				crossStrength: {ref: "swell_crossStrength"},
				crossWavelengthRatio: {ref: "swell_crossRatio"},
				asymm: {ref: "swell_asymm"},
				warpScale: {ref: "swell_warpScale"},
				warpOctaves: 3,
				warpAmount: {ref: "swell_warpAmount"},
				colorRamp: {ref: "swell_ramp"},
				heightScale: {ref: "swell_height"},
			},
			blend: "multiply",
			contributes: "both",
		}),
		// chop — relief only (no ramp, so the primitive emits a greyscale colour that a "height"
		// contribution discards). A second colour-writing train would compete with the swell's
		// shading for the same pixels and only muddy it.
		new MaterialLayer({
			id: "chop",
			primitive: "waves",
			args: {
				wavelength: {ref: "chop_wavelength"},
				angle: {ref: "chop_angle"},
				sharpness: 1,
				crossAngle: {ref: "chop_crossAngle"},
				crossStrength: {ref: "chop_crossStrength"},
				crossWavelengthRatio: {ref: "chop_crossRatio"},
				asymm: 1,
				warpScale: {ref: "chop_warpScale"},
				warpOctaves: 3,
				warpAmount: {ref: "chop_warpAmount"},
				heightScale: {ref: "chop_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		// capillary — the finest scale, relief only. Two octaves: a third would land near the
		// sampling limit at the zooms where this layer is visible at all, and only alias.
		new MaterialLayer({
			id: "capillary",
			primitive: "fbm",
			args: {
				scale: {ref: "capillary_scale"},
				octaves: 2,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				heightScale: {ref: "capillary_height"},
			},
			blend: "over",
			contributes: "height",
		}),
		// foam — DISABLED 2026-08-09. See the FOAM DISABLED note at the top of this file. Left in
		// place (commented) because the wiring is correct as far as the layer system is concerned —
		// the defect is in the coverageField primitive's saturating output, not here.
		// oxlint-disable-next-line lf/sc
		/*
		// foam — Set11 Phase 3: a COVERAGE FIELD (connected, ragged, streaky regions aligned
		// with the swell) instead of the old scatter lattice (evenly-spaced round confetti).
		// heightBlend so foam only shows where it stands above the accumulated surface, i.e. in
		// the bed's low patches (see THE FOAM GATE).
		new MaterialLayer({
			id: "foam",
			primitive: "coverageField",
			args: {
				scale: {ref: "foam_scale"},
				octaves: 4,
				mode: 3,                              // turbulence -> ragged, lacy
				anisotropy: {ref: "foam_streak"},    // stretched along the swell direction
				angle: {ref: "swell_angle"},         // foam streaks align with the swell
				threshold: {ref: "foam_threshold"},  // ~0.62 -> sparse
				edgeSoftness: 0.06,
				warpScale: {ref: "foam_warpScale"},
				warpOctaves: 3,
				warpAmount: {ref: "foam_warpAmount"},
				colorRamp: {ref: "foam_ramp"},
				heightScale: {ref: "foam_height"},
			},
			blend: "heightBlend",
			blendArgs: {softness: {ref: "foam_softness"}},
			contributes: "both",
		}),
		*/
	],
});